"use client";

import { useEffect, useRef, useState, type RefObject } from "react";

// ResizeObserver-driven column count for a fixed-card-width grid. Closes the
// gap flagged in name-card-pager.tsx's CARD_WIDTH/CARD_GAP comment: the grid
// was hardcoded to grid-cols-3 at every viewport width, wasting most of a
// desktop screen. Computes how many CARD_WIDTH+CARD_GAP cells fit the
// container's current width rather than assuming a fixed count.
export function useGridColumns(
  containerRef: RefObject<HTMLElement | null>,
  cardWidth: number,
  cardGap: number,
  opts?: { min?: number; max?: number }
): number {
  const min = opts?.min ?? 1;
  const max = opts?.max ?? Infinity;
  const [columns, setColumns] = useState(min);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    function compute(width: number) {
      const cols = Math.max(min, Math.min(max, Math.floor((width + cardGap) / (cardWidth + cardGap))));
      setColumns(cols);
    }

    // Initial measurement — ResizeObserver doesn't fire until something
    // actually changes, so without this the first render stays at `min`.
    compute(el.getBoundingClientRect().width);

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width === undefined) return;
      // Coalesce rapid-fire resize events (e.g. a drag-resize) onto the next
      // animation frame rather than recomputing on every single callback.
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => compute(width));
    });
    observer.observe(el);

    return () => {
      observer.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [containerRef, cardWidth, cardGap, min, max]);

  return columns;
}
